import { Card, List, Typography } from "antd";
import dayjs from "dayjs";
import {
  CheckCircle,
  Clock,
  Package,
  Truck,
  FileSignature,
  User,
} from "lucide-react";
import React, { useState } from "react";
import { useGetMyServiceTickets } from "~/services/service-ticket";
import { TServiceTicket } from "~/types";
import { capitalize, formatFullAddress } from "~/utils";
import { JobStatus, ServiceType } from "~/utils/constants";
import { TicketDetails } from "./ticket-details";

const { Title, Text } = Typography;

export const ServiceTickets: React.FC = () => {
  const [currentTicket, setCurrentTicket] = useState<TServiceTicket | null>(
    null
  );

  // Fetch service tickets assigned to the current user
  const {
    data: serviceTickets,
    isLoading,
    refetch,
  } = useGetMyServiceTickets({
    status: JobStatus.IN_PROGRESS,
  });

  const getCompletionStatusIndicators = (ticket: TServiceTicket) => {
    const indicators = [];

    if (ticket.timeIn && ticket.timeOut) {
      indicators.push(
        <div
          key="time"
          className="flex items-center space-x-1 text-xs text-green-600"
        >
          <Clock size={10} />
          <span>Time</span>
        </div>
      );
    }

    if (ticket.clientSignature) {
      indicators.push(
        <div
          key="signature"
          className="flex items-center space-x-1 text-xs text-green-600"
        >
          <FileSignature size={10} />
          <span>Signed</span>
        </div>
      );
    }

    if (ticket.staffCheckoff) {
      indicators.push(
        <div
          key="checkoff"
          className="flex items-center space-x-1 text-xs text-green-600"
        >
          <User size={10} />
          <span>Confirmed</span>
        </div>
      );
    }

    return indicators;
  };

  const handleSelectTicket = (ticket: TServiceTicket) => {
    setCurrentTicket(ticket);
  };

  const renderTicketList = () => {
    if (!serviceTickets?.items || serviceTickets.items.length === 0) {
      return (
        <div className="text-center py-8 md:py-10">
          <Text type="secondary" className="text-sm">
            No service tickets assigned for today
          </Text>
        </div>
      );
    }

    return (
      <List
        dataSource={serviceTickets.items}
        renderItem={(ticket) => {
          const Icon =
            ticket.serviceType === ServiceType.DELIVERY
              ? Truck
              : ticket.serviceType === ServiceType.COLLECTION
              ? Package
              : CheckCircle;

          return (
            <List.Item
              key={ticket.id}
              className={`cursor-pointer hover:bg-gray-50 transition-colors ${
                currentTicket?.id === ticket.id ? "bg-blue-50" : ""
              }`}
              onClick={() => handleSelectTicket(ticket)}
            >
              <div className="flex items-center w-full p-2">
                <div className="mr-2 md:mr-3 flex-shrink-0">
                  <Icon size={18} className="md:size-5" />
                </div>
                <div className="flex-grow min-w-0">
                  <div className="font-medium text-sm md:text-base">
                    <span
                      className={`mr-2 px-2 py-1 text-xs rounded-full ${
                        ticket.status === JobStatus.COMPLETED
                          ? "bg-green-100 text-green-800"
                          : ticket.status === JobStatus.IN_PROGRESS
                          ? "bg-blue-100 text-blue-800"
                          : "bg-gray-100 text-gray-800"
                      }`}
                    >
                      {capitalize(ticket.status)}
                    </span>
                    {ticket.serial}
                  </div>
                  <div className="text-xs md:text-sm text-gray-500 truncate">
                    {ticket.client
                      ? ticket.client.companyName || "Unnamed Client"
                      : "No client"}{" "}
                    - {formatFullAddress(ticket.location)}
                  </div>

                  {/* Completion Status Indicators */}
                  <div className="flex flex-wrap gap-1 mt-1">
                    {getCompletionStatusIndicators(ticket)}
                  </div>
                  <div className="text-xs md:text-sm font-medium">
                    {ticket.serviceDatetime
                      ? dayjs(ticket.startDatetime).format("h:mm A")
                      : "Any time"}
                  </div>
                </div>
              </div>
            </List.Item>
          );
        }}
      />
    );
  };

  if (isLoading) {
    return (
      <div className="space-y-3 md:space-y-4">
        <Title level={3} className="mb-0 text-lg md:text-xl ">
          Service Tickets
        </Title>
        <Card className="border-0 md:border">
          <div className="text-center py-8 md:py-10 xl:py-12">
            <Clock
              size={24}
              className="md:size-8 mx-auto mb-2 md:mb-3 text-gray-400 animate-pulse"
            />
            <Text type="secondary" className="text-sm md:text-base">
              Loading service tickets...
            </Text>
          </div>
        </Card>
      </div>
    );
  }

  return (
    <div className="space-y-3 md:space-y-4">
      <Title level={3} className="mb-0 text-lg md:text-xl ">
        Service Tickets
      </Title>
      <div className="grid grid-cols-1 xl:grid-cols-3 gap-3 md:gap-4 xl:gap-6 mt-2">
        <div className="xl:col-span-1">
          <Card classNames={{ body: "p-0" }} className="border-0 md:border">
            {renderTicketList()}
          </Card>
        </div>
        <div className="xl:col-span-2">
          <Card
            className="h-full border-0 md:border"
            classNames={{ body: "p-0" }}
          >
            <div className="bg-blue-50 p-3 md:p-4 xl:p-5 mb-0 border-b">
              <Title level={4} className="mb-1 text-base md:text-lg xl:text-xl">
                {currentTicket ? (
                  <>
                    {currentTicket.serial} -{" "}
                    {capitalize(currentTicket.serviceType)} Service
                  </>
                ) : (
                  "Service Details"
                )}
              </Title>
              {currentTicket && (
                <Text
                  type="secondary"
                  className="text-xs md:text-sm xl:text-base"
                >
                  {currentTicket.client
                    ? currentTicket.client.companyName || "Unnamed Client"
                    : "No client"}{" "}
                  - {currentTicket.location?.name || "Location"}
                </Text>
              )}
            </div>
            <TicketDetails
              ticket={currentTicket}
              refetch={refetch}
              setTicket={setCurrentTicket}
            />
          </Card>
        </div>
      </div>
    </div>
  );
};
